import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Search,
  Eye,
  ArrowLeft,
  Download,
  Mail,
  Send,
  AlertCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  Loader2
} from 'lucide-react';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { payslipsApi } from '../../../api/payslipsApi';
import { toast } from 'react-hot-toast';
import { formatDate } from '../../../utils/stringUtils';

import ExportModal from '../../../components/common/ExportModal';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
const PayslipBatchDetails = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [payslipPage, setPayslipPage] = useState(1);
  const [payslipSearch, setPayslipSearch] = useState('');
  const [debouncedPayslipSearch, setDebouncedPayslipSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [payslipPageSize, setPayslipPageSize] = useState(10);

  const baseExportFilters = useMemo(() => ({
    search: debouncedPayslipSearch,
    status: filterStatus,
    batchIds: batchId,
  }), [debouncedPayslipSearch, filterStatus, batchId]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds,
      overrideFilters,
      overridePage,
      overrideLimit,
    }) => {
      const filters = overrideFilters ?? baseExportFilters;
      const payload = {
        module: 'individual-payslips',
        scope,
        format,
        filters,
        page: overridePage ?? payslipPage,
        limit: overrideLimit ?? payslipPageSize,
        sortBy: sortField || 'createdAt',
        sortOrder: sortOrder || 'desc',
        selectedIds: selectedIds ?? [],
      };

      const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Export failed');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
      const fallbackName = `payslips-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, payslipPage, payslipPageSize, sortField, sortOrder]
  );

  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedPayslipSearch(payslipSearch.trim());
      setPayslipPage(1); // Reset to first page on search
    }, 400);
    return () => clearTimeout(handler);
  }, [payslipSearch]);

  // Fetch payslip details for the batch
  const {
    data: payslipDetailsData,
    isLoading: isDetailsLoading,
    refetch: refetchDetails
  } = useQuery({
    queryKey: ['payslipDetails', batchId, payslipPage, debouncedPayslipSearch, payslipPageSize],
    queryFn: async () => {
      if (!batchId) return { payslips: [], batch: null, pagination: null };
      // Use PayslipBatch details endpoint with pagination and search
      const response = await payslipsApi.getPayslipBatchDetails(
        batchId,
        payslipPage,
        payslipPageSize,
        debouncedPayslipSearch
      );


      // Extract data from response
      let payslips = [];
      let pagination = null;
      let batch = null;

      if (response && typeof response === 'object') {
        if (response.responseCode === 200 && response.data) {
          // Standard structure: { responseCode, responseMessage, data: { payslips, pagination, batch } }
          payslips = response.data.payslips || [];
          pagination = response.data.pagination || null;
          batch = response.data.batch || null;
        } else if (Array.isArray(response.data)) {
          // Fallback: response.data is array
          payslips = response.data;
        }
      }

      return {
        payslips: payslips,
        pagination: pagination || {
          page: payslipPage,
          limit: payslipPageSize,
          totalCount: payslips.length,
          totalPages: Math.ceil(payslips.length / payslipPageSize),
          hasNextPage: false,
          hasPrevPage: payslipPage > 1
        },
        batch: batch
      };
    },
    enabled: !!batchId,
    staleTime: 0,
    onError: (error) => {

      toast.error('Error loading payslip details');
    }
  });

  const payslipDetails = payslipDetailsData?.payslips || [];
  const batchInfo = payslipDetailsData?.batch || null;
  const payslipPagination = payslipDetailsData?.pagination || null;

  // Client-side filtering (search is handled by backend, but status filter is client-side)
  const filteredPayslipDetails = useMemo(() => {
    let filtered = payslipDetails;

    // Apply status filter (search is handled by backend)
    if (filterStatus !== 'all') {
      filtered = filtered.filter(payslip => payslip.status === filterStatus);
    }

    return filtered;
  }, [payslipDetails, filterStatus]);

  // Use backend pagination data, but apply client-side status filter
  const paginatedPayslips = filteredPayslipDetails;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedPayslips = useMemo(() => {
    if (!paginatedPayslips) return [];
    if (!sortField) return paginatedPayslips;

    return [...paginatedPayslips].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'employeeName':
          aValue = (a.employeeName || '').toLowerCase();
          bValue = (b.employeeName || '').toLowerCase();
          break;
        case 'employeeId':
          aValue = (a.employeeId || '').toLowerCase();
          bValue = (b.employeeId || '').toLowerCase();
          break;
        case 'department':
          aValue = (a.department || '').toLowerCase();
          bValue = (b.department || '').toLowerCase();
          break;
        case 'netPay':
          aValue = Number(a.netPay || 0);
          bValue = Number(b.netPay || 0);
          break;
        case 'presentDays':
          aValue = Number(a.presentDays !== undefined ? a.presentDays : (a.workingDays || 0));
          bValue = Number(b.presentDays !== undefined ? b.presentDays : (b.workingDays || 0));
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [paginatedPayslips, sortField, sortOrder]);

  // Reset selection when filters change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [debouncedPayslipSearch, filterStatus, payslipPage]);

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = sortedPayslips.map(p => p.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats(!showInlineSelectedFormats);
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportSelected = async (format) => {
    if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
    const ids = Array.from(selectedRows);
    if (!ids.length) {
      toast.error('No selected payslips found to export.');
      return;
    }
    setExportLoading(true);
    const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
    try {
      await requestBackendExport({
        scope: 'selected',
        format,
        selectedIds: ids,
      });
      toast.success(`${ids.length} payslip(s) exported!`, { id: toastId });
      setShowInlineSelectedFormats(false);
    } catch (error) {

      toast.error(error?.message || 'Export failed.', { id: toastId });
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportScoped = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      setExportLoading(true);
      const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
      try {
        if (scope === 'currentPage' && !sortedPayslips.length) {
          toast.error('No data on current page to export.', { id: toastId });
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.', { id: toastId });
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: { ...baseExportFilters, startDate, endDate },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: { batchIds: batchId },
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          toast.dismiss(toastId);
          return;
        }

        toast.success('Export completed successfully', { id: toastId });
        setShowExportModal(false);
      } catch (error) {

        toast.error(error?.message || 'Export failed.', { id: toastId });
      } finally {
        setExportLoading(false);
      }
    },
    [sortedPayslips, baseExportFilters, requestBackendExport, batchId]
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'withheld': return 'bg-red-100 text-red-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewPayslip = (payslip) => {
    if (!payslip?.id) return;
    navigate(`/payslips/batch/${batchId}/payslip/${payslip.id}`);
  };

  const sendPayslipEmailMutation = useMutation({
    mutationFn: async (payslipId) => {
      const response = await payslipsApi.sendPayslipEmail(payslipId);
      if (response && response.responseCode && response.responseCode !== 200) {
        throw new Error(response.responseMessage || 'Failed to send payslip email');
      }
      return response;
    },
    onSuccess: (data) => {
      const pdfAttached = data?.data?.pdfAttached ?? data?.pdfAttached;
      const message = pdfAttached
        ? 'Payslip email sent with PDF attachment!'
        : (data?.responseMessage || data?.message || 'Payslip email sent successfully');
      toast.success(message);
    },
    onError: (error) => {

      const errorMessage = error?.response?.data?.responseMessage
        || error?.response?.data?.message
        || error?.message
        || 'Failed to send payslip email. Please try again.';
      toast.error(errorMessage);
    }
  });

  const handleDownloadPayslip = async (payslipId) => {
    if (!payslipId) return;

    try {
      const response = await payslipsApi.getPayslipById(payslipId);
      const payslip =
        response?.responseCode === 200 && response?.data
          ? response.data
          : response?.data || response;

      if (!payslip?.id && !payslip?.payslipId) {
        toast.error('Payslip not found');
        return;
      }

      const downloadResponse = await payslipsApi.downloadPayslip(payslipId);

      const blob = downloadResponse.data instanceof Blob ? downloadResponse.data : (downloadResponse instanceof Blob ? downloadResponse : new Blob([downloadResponse]));
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Fallback filename
      let fileName = `Payslip_${payslip.employeeName?.replace(/\s+/g, '_') || 'Employee'}_${payslip.employeeId || payslipId}.pdf`;

      // Try to get filename from content-disposition header if available
      if (downloadResponse.headers && downloadResponse.headers['content-disposition']) {
        const match = downloadResponse.headers['content-disposition'].match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);


      toast.success('Payslip downloaded successfully!');
    } catch (error) {

      toast.error(`Failed to generate payslip PDF: ${error.message || 'Unknown error occurred. Please try again later.'}`);
    }
  };

  // Confirmation state for single payslip email send
  // Shape: { payslipId, employeeName, employeeId } | null
  const [confirmSendPayslip, setConfirmSendPayslip] = useState(null);

  const handleSendEmail = (payslip) => {
    if (!payslip?.id) return;
    setConfirmSendPayslip({
      payslipId: payslip.id,
      employeeName: payslip.employeeName || payslip.name || 'this employee',
      employeeId: payslip.employeeId || ''
    });
  };

  const handleConfirmSendEmail = () => {
    if (!confirmSendPayslip?.payslipId) return;
    const { payslipId } = confirmSendPayslip;
    setConfirmSendPayslip(null);
    // Backend generates the PDF server-side using generatePayslipPDFNode
    sendPayslipEmailMutation.mutate(payslipId);
  };


  return (
    <div className="space-y-6">
      {/* Header with back button, title, and search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                icon={<ArrowLeft className="h-4 w-4" />}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              {batchInfo && (
                <div className="flex flex-col">
                  <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                    {batchInfo.payGroupName} - {batchInfo.payrollMonth}
                  </h1>
                  {(batchInfo.generatedDate || batchInfo.createdAt) && (
                    <p className="text-xs font-medium text-gray-500 mt-1 flex items-center">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                      Generated on: {formatDate(batchInfo.generatedDate || batchInfo.createdAt, { includeTime: true })}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <PermissionGate permission="payroll" action="read">
                <div className="relative">
                  <Button
                    variant="outline"
                    icon={exportLoading ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> : <Download className="h-4 w-4" />}
                    onClick={handleExportClick}
                    disabled={exportLoading}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    Export {selectedRows.size > 0 ? `(${selectedRows.size})` : ''}
                    <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${showInlineSelectedFormats ? 'rotate-180' : ''}`} />
                  </Button>

                  <SelectedExportFormatBar
                    isOpen={showInlineSelectedFormats}
                    onClose={() => setShowInlineSelectedFormats(false)}
                    onExport={handleExportSelected}
                    loading={exportLoading}
                    className="absolute right-0 mt-2 z-50 min-w-[200px]"
                  />
                </div>
              </PermissionGate>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={payslipSearch}
                  onChange={(e) => setPayslipSearch(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payslip Details Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
                    checked={sortedPayslips.length > 0 && selectedRows.size === sortedPayslips.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-xs cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('employeeName')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Employee</span>
                    {sortField === 'employeeName' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-blue-600" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[120px] cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('employeeId')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Employee ID</span>
                    {sortField === 'employeeId' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-blue-600" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[200px] cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('department')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Department</span>
                    {sortField === 'department' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-blue-600" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('netPay')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Net Pay</span>
                    {sortField === 'netPay' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-blue-600" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('presentDays')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Present Days</span>
                    {sortField === 'presentDays' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-blue-600" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {sortField === 'status' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-blue-600" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isDetailsLoading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-agrosaf-green"></div>
                      <span className="ml-2 text-gray-500">Loading payslip details...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedPayslips.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    {filteredPayslipDetails.length === 0 ? 'No payslips found' : 'No payslips on this page'}
                  </td>
                </tr>
              ) : (
                sortedPayslips.map((payslip) => (
                  <tr key={payslip.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
                        checked={selectedRows.has(payslip.id)}
                        onChange={() => handleSelectRow(payslip.id)}
                      />
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <div className="text-sm font-medium text-gray-900 truncate" title={payslip.employeeName || 'N/A'}>
                        {payslip.employeeName || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500 truncate" title={payslip.email || 'N/A'}>
                        {payslip.email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-[120px]">
                      <div className="text-sm text-gray-900 truncate" title={payslip.employeeId || 'N/A'}>
                        {payslip.employeeId || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-[200px]">
                      <div className="text-sm text-gray-900 truncate" title={payslip.department || 'N/A'}>
                        {payslip.department || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ₹{(payslip.netPay || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payslip.presentDays !== undefined ? payslip.presentDays : (payslip.workingDays || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(payslip.status)}`}>
                        {payslip.status || 'draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <PermissionGate permission="payroll" action="read">
                          <button
                            onClick={() => handleViewPayslip(payslip)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Payslip"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="payroll" action="read">
                          <button
                            onClick={() => handleDownloadPayslip(payslip.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Download Payslip"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="payroll" action="create">
                          <button
                            onClick={() => handleSendEmail(payslip)}
                            disabled={sendPayslipEmailMutation.isPending}
                            className="text-purple-600 hover:text-purple-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Send Payslip Email"
                          >
                            <Mail className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {payslipPagination && (payslipPagination.totalCount > 0 || payslipPagination.totalItems > 0) && (
          <div className="border-t border-gray-200 px-6 pb-4">
            <PaginationControls
              currentPage={payslipPagination.page || payslipPagination.currentPage || 1}
              totalPages={payslipPagination.totalPages || 1}
              totalItems={payslipPagination.totalCount || payslipPagination.totalItems || 0}
              limit={payslipPagination.limit || payslipPageSize}
              hasNextPage={payslipPagination.hasNextPage}
              hasPrevPage={payslipPagination.hasPrevPage}
              onPageChange={setPayslipPage}
              onLimitChange={(newLimit) => {
                setPayslipPageSize(newLimit);
                setPayslipPage(1);
              }}
            />
          </div>
        )}
      </div>

      {/* ── Payslip Email Confirmation Dialog ────────────────────────────── */}
      {confirmSendPayslip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm !mt-0">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Send Payslip Email</h3>
                <p className="text-xs text-gray-500 mt-0.5">A payslip PDF will be generated and attached</p>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-sm text-gray-700">
                You are about to send a payslip to{' '}
                <strong className="text-purple-700">{confirmSendPayslip.employeeName}</strong>
                {confirmSendPayslip.employeeId && (
                  <span className="text-gray-500"> ({confirmSendPayslip.employeeId})</span>
                )}.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => setConfirmSendPayslip(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSendEmail}
                className="px-5 py-2 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                Yes, Send Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title={`Export Payslips - ${batchInfo?.payGroupName || 'Batch'}`}
        scopedFormatExport={{
          onExport: handleExportScoped,
          showDateRange: false,
        }}
      />
    </div>
  );
};

export default PayslipBatchDetails;

